/**
 * KindVoicePanel.jsx
 *
 * Who reads each KIND of utterance — a sign, a tannoy, a scrawl on a wall, an unspoken thought.
 *
 * ⭐ THE SECOND HALF OF A DECISION. Marking a line "Signage" in the table below says what the line
 * IS; this says whose voice reads it. Neither half does anything alone, and the first half is the
 * one that feels like progress — which is why the preflight counts kinds with no voice and says so
 * before the spend.
 *
 * ⚠️ THIS IS A SERIES-WIDE CHOICE, and the panel says so in as many words. "Signage is read by the
 * Narrator" is true of a Literary Group, not of a chapter: set once while working the first book,
 * inherited by every book added afterwards. The panel sits inside a per-BOOK page, so an operator
 * editing it from one book is changing all of them — the same scope, and the same warning, as the
 * pronunciation register it sits beside.
 *
 * NOTHING IS CREATED HERE. The picker offers characters that already exist, exactly as
 * SectionVoicePanel does, because a kind lends a voice and never an identity: no tier, no archetype,
 * no roster entry. If a line is really spoken by someone the book names, that is a REASSIGNMENT, and
 * the kind then supplies only the delivery.
 *
 * See docs/journal/UTTERANCE_KINDS_A_SIGN_IS_NOT_A_SPEAKER_SPEC.md.
 */

import React, { useCallback, useState } from "react";
import { Dropdown, Icon, Label, Message, Popup, Segment } from "semantic-ui-react";
import { apiClient } from "../../apiTools/apiClient";

const NONE = "__none__";

/**
 * ⛔ THE ROSTER ARRIVES WITH THE KINDS, and must not be fetched from a book.
 *
 * This picker was empty on first use. It had borrowed `GET /voice-rendering/:bookId/characters`,
 * the endpoint SectionVoicePanel uses, which matches `(b:Book)-[:HAS_CHARACTER]->(c)` — and this
 * architecture hangs characters off the LITGROUP. The Luck of Madonna 13 returns 0 characters that
 * way and 79 the right way. A LitGroup-scoped decision cannot take its options from a book.
 */
const KindVoicePanel = ({ litGroupUuid, kinds, characters, onChanged }) => {
  const [saving, setSaving] = useState(null);
  const [saveError, setSaveError] = useState(null);

  /**
   * ⚠️ THE WHOLE MAP IS SENT, every time — the endpoint is wholesale on purpose, following the
   * pronunciation register and the hyphen verdicts. Sending one entry without merging first is the
   * bug that discarded 45 of the operator's own decisions in `saveHyphenVerdicts`.
   */
  const choose = useCallback(async (kind, value) => {
    if (!litGroupUuid || !kinds) return;
    const next = {};
    kinds.forEach((k) => { if (k.characterUuid) next[k.kind] = k.characterUuid; });
    if (value === NONE) delete next[kind];
    else next[kind] = value;

    setSaving(kind);
    setSaveError(null);
    try {
      await apiClient.put(`/litgroups/${litGroupUuid}/kind-voices`, { kindVoices: next });
      if (onChanged) await onChanged();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.response?.data?.error?.message
        || err.message || "Could not save");
    } finally {
      setSaving(null);
    }
  }, [litGroupUuid, kinds, onChanged]);

  if (!litGroupUuid || !kinds || !kinds.length) return null;

  const options = [
    { key: NONE, value: NONE, text: "— no voice —" },
    ...(characters || [])
      .filter((c) => c && c.uuid && c.name)
      // The tier is shown because the choice is usually "who can spare a voice for this" — a lead
      // reading the signage is a decision, a walk-on doing it is usually the intended one.
      .map((c) => ({
        key: c.uuid,
        value: c.uuid,
        text: c.name,
        description: c.castingTier || undefined,
      })),
  ];

  const assignedCount = kinds.filter((k) => k.characterUuid).length;

  return (
    <Segment className="rs-kind-voices" style={{ marginTop: "1rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: ".6rem", marginBottom: ".25rem" }}>
        <div style={{ fontWeight: 600 }}>Who reads what kind</div>
        <Label size="tiny" basic>{assignedCount} of {kinds.length} voiced</Label>
      </div>

      {/*
        SAID PLAINLY, ONCE, ABOVE THE CONTROLS. The panel is inside a book, the effect is on the
        series, and that mismatch is exactly the sort of thing people read past.
      */}
      <div style={{ opacity: 0.7, fontSize: ".9em", marginBottom: ".75rem" }}>
        <Icon name="sitemap" /> These choices belong to the whole Literary Group — every book in it,
        including books added later. A line marked with a kind but given no voice changes nothing.
      </div>

      {!characters || !characters.length ? (
        // NOT AN EMPTY DROPDOWN. An empty picker looks like a broken panel, which is exactly how
        // this arrived the first time — say which list is missing instead.
        <Message size="tiny" warning>
          <Icon name="warning sign" /> No characters found for this Literary Group, so there is
          nobody to lend a voice yet.
        </Message>
      ) : null}
      {saveError && <Message size="tiny" negative><Icon name="warning sign" /> {String(saveError)}</Message>}

      {kinds.map((k, i) => (
        <div
          key={k.kind}
          // Same stacking trick SectionVoicePanel needs: an open menu is positioned inside its own
          // row, so a LATER row paints over it unless earlier rows sit higher.
          style={{
            display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".4rem",
            position: "relative", zIndex: kinds.length - i,
          }}
        >
          <div style={{ minWidth: "11rem" }}>
            <Popup
              size="mini"
              content={k.hint}
              trigger={<strong style={{ cursor: "help" }}>{k.label}</strong>}
            />
            <div style={{ opacity: 0.55, fontSize: ".85em" }}>
              {k.hasTreatment ? "has its own delivery" : "plain delivery"}
              {k.quoted ? " · quoted in the text" : ""}
            </div>
          </div>

          <Dropdown
            selection
            search
            disabled={!characters || !characters.length || saving === k.kind}
            loading={saving === k.kind}
            options={options}
            value={k.characterUuid || NONE}
            onChange={(_, d) => choose(k.kind, d.value)}
            style={{ minWidth: "14rem" }}
          />

          {/*
            AN ALL-CLEAR AS WELL AS A WARNING, for the reason SectionVoicePanel gives: a panel that
            only speaks when something is wrong is indistinguishable from one that never ran.
            `dangling` is its own case — a voice WAS chosen and the character is gone, which is not
            the same as never having chosen, and only one of the two is a decision anyone made.
          */}
          <span style={{ opacity: 0.8, fontSize: ".9em" }}>
            {k.dangling ? (
              <><Icon name="warning sign" color="orange" />
                The character chosen for this no longer exists — these lines have quietly gone back
                to nobody. Pick again.</>
            ) : k.characterName ? (
              <><Icon name="check" color="green" />{k.characterName} reads these.</>
            ) : (
              <><Icon name="microphone slash" />No voice — lines nobody speaks stay silent.</>
            )}
          </span>
        </div>
      ))}

      <div style={{ opacity: 0.6, fontSize: ".85em", marginTop: ".5rem" }}>
        Applied on the next render — no re-ingest needed.
      </div>
    </Segment>
  );
};

export default KindVoicePanel;
